import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { formatPlayCount } from '../lib/utils';

const card = { boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' };

const Hunters = () => {
  const navigate = useNavigate();
  const [hunters, setHunters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => { loadCampaigns(); }, []); // eslint-disable-line

  useEffect(() => {
    if (selectedCampaign) loadHunters(selectedCampaign);
    else loadAllHunters();
  }, [selectedCampaign]); // eslint-disable-line

  const loadCampaigns = async () => {
    try {
      const data = await api.get('/api/campaigns');
      setCampaigns(Array.isArray(data) ? data : []);
    } catch { setCampaigns([]); }
  };

  const loadAllHunters = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/hunters');
      setHunters(Array.isArray(data) ? data : []);
    } catch (error) {
      alert('Error loading hunters: ' + error.message);
      setHunters([]);
    } finally { setLoading(false); }
  };

  const loadHunters = async (campaignId) => {
    setLoading(true);
    try {
      const data = await api.get(`/api/hunters?campaign_id=${campaignId}`);
      setHunters(Array.isArray(data) ? data : []);
    } catch (error) {
      alert('Error loading hunters: ' + error.message);
      setHunters([]);
    } finally { setLoading(false); }
  };

  const getStatusCounts = (hunter) => {
    const c = { pending: 0, approved: 0, monitored: 0, rejected: 0, total: 0 };
    hunter.proposals?.forEach(p => { c[p.status] = (c[p.status] || 0) + 1; c.total += 1; });
    return c;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">Hunters</h1>
        <span className="text-[13px] text-[#8a8a8a]">{hunters.length} hunter</span>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-2xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" style={card}>
        <span className="text-[13px] font-medium text-[#646464] flex-shrink-0">Filter:</span>
        <select
          value={selectedCampaign}
          onChange={(e) => setSelectedCampaign(e.target.value)}
          className="px-3 py-1.5 text-[13px] border border-[#e5e5e5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3fb5d2] text-[#3b3b3b] bg-white w-full min-w-0 sm:min-w-[200px] sm:w-auto max-w-full"
        >
          <option value="">All Campaigns</option>
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {hunters.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center" style={card}>
          <p className="text-[14px] text-[#8a8a8a]">Tidak ada hunter ditemukan.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl overflow-hidden" style={card}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#e5e5e5]">
                  {['Hunter', 'Email', 'Role', 'Tier', 'Total', 'Status', 'Campaigns', 'Actions'].map(h => (
                    <th key={h} className="text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wider px-4 py-3 text-left whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {hunters.map((hunter) => {
                  const sc = getStatusCounts(hunter);
                  const initials = hunter.full_name?.charAt(0)?.toUpperCase() || '?';
                  return (
                    <tr key={hunter.user_id} className="border-b border-[#e5e5e5] last:border-0 hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-[#ecf8fb] flex items-center justify-center text-[12px] font-bold text-[#2a798c] flex-shrink-0">
                            {initials}
                          </div>
                          <span className="text-[13px] font-medium text-[#3b3b3b] whitespace-nowrap">
                            {hunter.full_name || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[12px] text-[#8a8a8a] whitespace-nowrap">{hunter.email}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#ecf8fb] text-[#2a798c] border border-[#3fb5d2]/30 capitalize">
                          {hunter.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {hunter.tier ? (
                          <div>
                            <span className="text-[12px] font-semibold text-[#3fb5d2]">{hunter.tier.nama}</span>
                            <p className="text-[11px] text-[#8a8a8a]">{formatPlayCount(hunter.total_play_count || 0)} views</p>
                          </div>
                        ) : (
                          <span className="text-[12px] text-[#8a8a8a]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[13px] font-semibold text-[#3b3b3b] whitespace-nowrap">{sc.total}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {sc.pending   > 0 && <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-amber-50 text-amber-700 border border-amber-200">P:{sc.pending}</span>}
                          {sc.approved  > 0 && <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-blue-50 text-blue-600 border border-blue-200">A:{sc.approved}</span>}
                          {sc.monitored > 0 && <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-green-50 text-green-700 border border-green-200">M:{sc.monitored}</span>}
                          {sc.rejected  > 0 && <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-red-50 text-[#c62828] border border-red-200">R:{sc.rejected}</span>}
                          {sc.total === 0 && <span className="text-[11px] text-[#8a8a8a]">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {hunter.campaigns?.slice(0, 2).map((c) => (
                            <span key={c.id} className="px-1.5 py-0.5 text-[10px] rounded bg-gray-100 text-[#646464] whitespace-nowrap">
                              {c.name}
                            </span>
                          ))}
                          {(hunter.campaigns?.length || 0) > 2 && (
                            <span className="px-1.5 py-0.5 text-[10px] rounded bg-gray-100 text-[#646464]">
                              +{hunter.campaigns.length - 2}
                            </span>
                          )}
                          {(!hunter.campaigns || hunter.campaigns.length === 0) && <span className="text-[11px] text-[#8a8a8a]">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button onClick={() => navigate(`/hunters/${hunter.user_id}`)}
                          className="text-[12px] font-medium text-[#3fb5d2] hover:text-[#2a798c] transition-colors">
                          Detail →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hunters;
